import { observable, action, computed } from 'mobx';
import { UserProfile } from './Models';
import UrlService from './Services/UrlService';

class AppStore {
    public readonly isPrintMode: boolean = this.printMode;
    @observable isLoading: boolean = false;
    ajaxCount: number = 0;

    @observable isHistorized: boolean = false;
    @observable isDisabledChangeLog: boolean = true;
    @observable isCtrlKey: boolean = false;
    @observable isShiftKey: boolean = false;

    actionElement: HTMLInputElement | null = null;
    appLoaderElement: HTMLElement;
    appLoaderMessageElement: HTMLElement;
    actionElementMap: Map<number, HTMLInputElement> = new Map<number, HTMLInputElement>();

    constructor() {
        document.addEventListener('click', this.appClickHandler);
    }

    public get isMacOS(): boolean {
        return window.navigator.platform.includes('Mac');
    }

    public get currentToken(): string {
        return this.getValue<string>('currentToken') || '';
    }

    public get currentUser(): UserProfile | null {
        return this.getValue<UserProfile>('currentUser');
    }

    public get impersonatedUser(): IImpersonationModel | null {
        return this.getValue<IImpersonationModel>('impersonatedUser');
    }

    public get currentUserRoles(): string[] {
        return (this.impersonatedUser && this.impersonatedUser.role && [this.impersonatedUser.role]) || (this.currentUser && this.currentUser.roles) || [];
    }

    public get realUserRoles(): string[] {
        return (this.currentUser && this.currentUser.roles) || [];
    }
    
    @action
    showLoading() {
        this.ajaxCount++;
        if (!this.isLoading) {
            this.isLoading = true;
        }
    }

    @action
    hideLoading() {
        this.ajaxCount--;
        if (this.ajaxCount === 0) {
            this.isLoading = false;
        }
    }

    @computed
    get isAuthorized  (): boolean {
        return !!(this.currentToken && this.currentUser);
    }

    @action
    clearUserData() {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentToken');
        localStorage.removeItem('impersonatedUser');
    }

    @action
    setValue<T>(key: string, value: T) {
        localStorage.setItem(key, JSON.stringify(value));
    }

    @action
    getValue<T>(key: string): T | null {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) as T : null;
    }

    showAjaxSpinner(timestamp: number) {
        const elem = this.actionElement;
        if (elem) {
            this.actionElementMap.set(timestamp, elem);
            elem.disabled = true;
            elem.classList.add('btn-loader');
        }
    }

    hideAjaxSpinner(timestamp: number) {
        const elem = this.actionElementMap.get(timestamp);
        if (elem) {
            elem.disabled = false;
            elem.classList.remove('btn-loader');
            this.actionElementMap.delete(timestamp);
            this.actionElement = null;
        }
    }

    hideAppLoader() {
        if (!this.appLoaderElement.classList.contains('hide')) {
            this.appLoaderElement.classList.add('hide');
            this.appLoaderMessageElement.innerText = '';
        }
    }

    showAppLoader(message?: string) {
        this.appLoaderElement.classList.remove('invisible');
        setTimeout(() => {
            this.appLoaderElement.classList.remove('hide');
            if (message) {
                this.appLoaderMessageElement.innerText = message;
            }
        },0);
    }

    appClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLInputElement;
        if (target && target.type === 'button') {
            this.actionElement = target;
        }
    };

    @computed get printMode() { return UrlService.getParam('printMode') === 'enabled'; }
}

interface IImpersonationModel {
    displayName?: string;
    personId?: string;
    role?: string;
    impersonateAsPrincipal?: boolean;
}

export const appStore = new AppStore();
